<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="currencyRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="currencyList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCurrency"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="currency-code"
            v-model="filter.code"
            :label="$t('Currency Code')"
            :placeholder="$t('Select')"
            item="label"
            :options="currencyCodeOptions"
            :reduce="option => option"
          />
        </b-col>

        <!-- Start Date-->
        <b-col md="6">
          <DatePickerField
            id="start-date"
            v-model="filter.startDate"
            :min-date="new Date()"
            :label="$t('Start Date')"
            :config="datePickerConfig"
            :placeholder="$t('Select Date')"
          />
        </b-col>
        <!--  Start Date -->

        <!-- End Date-->
        <b-col md="6">
          <DatePickerField
            id="end-date"
            v-model="filter.endDate"
            :min-date="new Date()"
            :label="$t('End Date')"
            :config="datePickerConfig"
            :placeholder="$t('Select Date')"
          />
        </b-col>
        <!--  End Date -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            item="label"
            :options="translatedStatusOptions"
            :reduce="option => option.value"
          />
        </b-col>
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge,
} from 'bootstrap-vue'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import { toDateWithYear } from '../../../../helpers/formatDate'
import { operationPermissions } from '../../../../helpers/permissions'

const statusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BModal,
    BBadge,
    BSpinner,
    TablePagination,
    TableEntries,
    SelectField,
    InputField,
    FullScreenSpinner,
    Button,
    DatePickerField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      sortBy: 'name',
      required,
      toDateWithYear,
      sortDesc: false,
      showSpinner: false,
      getStatusColor,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'country', sortable: true, label: 'Country' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'symbol', sortable: true, label: 'Symbol' },
        { key: 'currencyFlag', sortable: true, label: 'Flag' },
        { key: 'currencyType', sortable: true, label: 'Currency Type' },
        { key: 'status', label: 'Status' },
      ],
      filter: {
        name: '',
        code: '',
        status: '',
        startDate: '',
        endDate: '',
      },
      currencyList: [],
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        defaultDate: '',
        ariaDateFormat: 'd/m/Y',
      },
      currencyCodeOptions: [],
      // statusOptions: [
      //   'Active',
      //   'Inactive',
      // ],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCurrency()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCurrency()
    },
  },
  mounted() {
    this.fetchCurrency()
    this.fetchAllCurrencies()
  },
  methods: {
    ...mapActions({
      getCurrencies: 'operations/getCurrencies',
      getAllCurrencies: 'account/getAllCurrenciesAction',
    }),
    async fetchCurrency() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getCurrencies({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: operationPermissions.VIEW_CURRENCY,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.currencyList = data.data
          this.totalCount = data.recordCount
        } else {
          this.currencyList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({ permission: operationPermissions.VIEW_CURRENCY })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },

    resetFilters() {
      this.filter.name = ''
      this.filter.code = ''
      this.filter.status = ''
      this.filter.startDate = ''
      this.filter.endDate = ''
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCurrency()
    },
  },
}
</script>
